import { httpClient } from '../httpClient';

export const discoveryApi = {
  getSources: (params) =>
    httpClient.get('/data-discovery/', { params }),

  getSourceById: (id) =>
    httpClient.get(`/data-discovery/source/${id}`),

  createSource: (data) =>
    httpClient.post('/data-discovery/connect', data),

  updateSource: (id, data) =>
    httpClient.put(`/data-discovery/${id}`, data),

  deleteSource: (id) =>
    httpClient.delete(`/data-discovery/${id}`),

  syncSource: (id, data) =>
    httpClient.post(`/data-discovery/${id}/sync`, data),

  getSyncStatus: (sourceId) =>
    httpClient.get(`/data-discovery/${sourceId}/sync-status`),

  getSyncJobStatus: (jobId) =>
    httpClient.get(`/data-discovery/sync-jobs/${jobId}/status`),

  getHierarchy: (sourceId) =>
    httpClient.get(`/data-discovery/${sourceId}/hierarchy`),

  getCredentials: () =>
    httpClient.get('/settings/database-credentials'),

  getSyncSchedule: () =>
    httpClient.get('/data-discovery/sync/schedule'),

  updateSyncSchedule: (config) =>
    httpClient.put('/data-discovery/sync/schedule', config),

  refreshInfrastructure: () =>
    httpClient.post('/data-discovery/refresh-infrastructure'),

  disconnectSource: (id) =>
    httpClient.post(`/data-discovery/${id}/disconnect`),

  reconnectSource: (id) =>
    httpClient.post(`/data-discovery/${id}/reconnect`),

  connectDiscoveredSource: (sourceId, data) =>
    httpClient.post(`/data-discovery/${sourceId}/connect-discovered`, data),

  exportData: (format = 'csv') =>
    httpClient.get(`/data-discovery/export?format=${format}`,
      format === 'pdf' ? { responseType: 'blob' } : undefined),

  testConnection: (data) =>
    httpClient.post('/data-discovery/test-connection', data),

  updateServer: (serverId, data) =>
    httpClient.put(`/data-discovery/servers/${serverId}`, data),

  bulkServerAction: (serverId, body) =>
    httpClient.post(`/data-discovery/servers/${serverId}/bulk-action`, body),

  bulkEngineAction: (engineId, body) =>
    httpClient.post(`/data-discovery/engines/${engineId}/bulk-action`, body),

  updateServerScanProfile: (serverId, profile) =>
    httpClient.patch(`/data-discovery/servers/${serverId}/scan-profile`, {
      default_scan_profile: profile,
    }),

  // ── Categories ─────────────────────────────────────────────────────────
  getCategories: () =>
    httpClient.get('/data-discovery/categories'),

  createCategory: (data) =>
    httpClient.post('/data-discovery/categories', data),

  updateCategory: (id, data) =>
    httpClient.put(`/data-discovery/categories/${id}`, data),

  deleteCategory: (id) =>
    httpClient.delete(`/data-discovery/categories/${id}`),

  // ── Bulk Metadata ──────────────────────────────────────────────────────
  bulkUpdateMetadata: (data) =>
    httpClient.post('/data-discovery/bulk-metadata', data),

  csvPreview: (formData) =>
    httpClient.post('/data-discovery/bulk-metadata/csv-preview', formData, { isFormData: true }),

  csvApply: (data) =>
    httpClient.post('/data-discovery/bulk-metadata/csv-apply', data),

  getMetadataSuggestions: (field) =>
    httpClient.get(`/data-discovery/metadata/suggestions?field=${field}`),

  // ── Auto-discover ──────────────────────────────────────────────────────
  discover: (data) =>
    httpClient.post('/data-discovery/discover', data),

  getScanStatusById: (scanId) =>
    httpClient.get(`/data-discovery/scan-status/${scanId}`),

  getNetworkInterfaces: () =>
    httpClient.get('/data-discovery/network-interfaces'),

  // ── Sync Jobs ──────────────────────────────────────────────────────────
  cancelSyncJob: (jobId) =>
    httpClient.post(`/data-discovery/sync-jobs/${jobId}/cancel`),

  retrySyncJob: (jobId, data) =>
    httpClient.post(`/data-discovery/sync-jobs/${jobId}/retry`, data),

  getSyncJobs: (queryString) =>
    httpClient.get(`/data-discovery/sync-jobs?${queryString}`),

  // ── Schema Changes ──────────────────────────────────────────────────────────
  getSchemaHistory: (sourceId, params) =>
    httpClient.get(`/data-discovery/${sourceId}/schema-history`, { params }),

  getSchemaChanges: (params) =>
    httpClient.get('/data-discovery/schema-changes', { params }),

  // ── Minimization ────────────────────────────────────────────────
  createAssessment: (data) =>
    httpClient.post('/data-discovery/minimization/assessments', data),

  listAssessments: (params) =>
    httpClient.get('/data-discovery/minimization/assessments', { params }),

  getAssessment: (id) =>
    httpClient.get(`/data-discovery/minimization/assessments/${id}`),

  updateFieldJustification: (assessmentId, fieldId, data) =>
    httpClient.patch(`/data-discovery/minimization/assessments/${assessmentId}/fields/${fieldId}`, data),

  completeAssessment: (id, data) =>
    httpClient.post(`/data-discovery/minimization/assessments/${id}/complete`, data),

  deleteAssessment: (id) =>
    httpClient.delete(`/data-discovery/minimization/assessments/${id}`),

  archiveAssessment: (id) =>
    httpClient.post(`/data-discovery/minimization/assessments/${id}/archive`),

  exportAssessment: (id, format = 'pdf') =>
    httpClient.get(`/data-discovery/minimization/assessments/${id}/export?format=${format}`, { responseType: 'blob' }),

  getPurposeMappings: () =>
    httpClient.get('/data-discovery/minimization/purpose-mappings'),

  updatePurposeMappings: (data) =>
    httpClient.put('/data-discovery/minimization/purpose-mappings', data),

  // §15.12 / QA #17 — Art. 5 findings summary for the landing banner.
  getMinimizationFindings: () =>
    httpClient.get('/data-discovery/minimization/findings'),

  getDepartmentOverview: () =>
    httpClient.get('/data-discovery/minimization/department-overview'),

  getJustificationGaps: (params) =>
    httpClient.get('/data-discovery/minimization/justification-gaps', { params }),

  // ── Command Center ──────────────────────────────────────────────
  getCommandCenterData: () =>
    httpClient.get('/data-discovery/command-center'),

  // ── NDMO ────────────────────────────────────────────────────────
  exportNdmoCatalog: (params) =>
    httpClient.get('/data-discovery/export/ndmo', { params, responseType: 'blob' }),

  previewNdmoCatalog: (params) =>
    httpClient.get('/data-discovery/export/ndmo', { params: { ...params, preview: true } }),

  getNdmoRegions: () =>
    httpClient.get('/data-discovery/ndmo/regions'),

  getCountries: () =>
    httpClient.get('/data-discovery/countries'),

  // ── Soft-Delete ─────────────────────────────────────────────────
  restoreSource: (id) =>
    httpClient.post(`/data-discovery/sources/${id}/restore`),

  // ── Cross-Module Integration ────────────────────────────────────
  getMinimizationSummary: (sourceId) =>
    httpClient.get(`/data-discovery/sources/${sourceId}/minimization-summary`),

  getPiiLocations: (params) =>
    httpClient.get('/data-discovery/sources/pii-locations', { params }),

  // -- Governance ---------------------------------------------------------

  getGovernanceStats: () =>
    httpClient.get('/data-discovery/governance/stats'),

  getDepartments: () =>
    httpClient.get('/data-discovery/governance/departments'),

  createDepartment: (data) =>
    httpClient.post('/data-discovery/governance/departments', data),

  updateDepartment: (id, data) =>
    httpClient.put(`/data-discovery/governance/departments/${id}`, data),

  deleteDepartment: (id) =>
    httpClient.delete(`/data-discovery/governance/departments/${id}`),

  getDepartmentStats: (id) =>
    httpClient.get(`/data-discovery/governance/departments/${id}/stats`),

  getPolicies: (params) =>
    httpClient.get('/data-discovery/governance/policies', { params }),

  createPolicy: (data) =>
    httpClient.post('/data-discovery/governance/policies', data),

  updatePolicy: (id, data) =>
    httpClient.put(`/data-discovery/governance/policies/${id}`, data),

  deletePolicy: (id) =>
    httpClient.delete(`/data-discovery/governance/policies/${id}`),

  applyPolicy: (id, data) =>
    httpClient.post(`/data-discovery/governance/policies/${id}/apply`, data),

  evaluatePolicies: () =>
    httpClient.post('/data-discovery/governance/policies/evaluate'),

  getPolicyMatches: (id) =>
    httpClient.get(`/data-discovery/governance/policies/${id}/matches`),

  previewPolicyMatches: (conditions) =>
    httpClient.post('/data-discovery/governance/policies/matches', { conditions }),

  rollbackPolicyApplication: (applicationId) =>
    httpClient.post(`/data-discovery/governance/policies/applications/${applicationId}/rollback`),

  getDepartmentNecessityMap: (deptId) =>
    httpClient.get(`/data-discovery/minimization/department/${deptId}/map`),

  getDepartmentReportPdf: (deptId) =>
    httpClient.get(`/data-discovery/governance/departments/${deptId}/report.pdf`, {
      responseType: 'blob',
    }),

  markDepartmentReviewed: (id) =>
    httpClient.patch(`/data-discovery/governance/departments/${id}/review`),

  reapplyDiscoveryPolicies: () =>
    httpClient.post('/data-discovery/governance/policies/reapply-discovery'),
};
